import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Container,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SideBar from "../dashboard admin/components/SideBar"; // Importando o Sidebar
import api from "../../../services/api/api";

const EditUserAdmin = () => {
  const { userId } = useParams(); // Pega o userId da URL
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState(""); // Estado para nova senha

  // As 4 opções fixas para roles
  const roles = [
    "journalist",
    "publisher",
    "admin",
    "journalist,publisher", // Combinação de roles permitida
  ];

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  // Buscar os dados do usuário com base no userId
  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      api
        .get("api/getUsers", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          const foundUser = response.data.users.find(
            (user) => user.id === userId
          );
          setUser(foundUser);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Erro ao buscar os usuários:", error);
          setLoading(false);
          
          // SweetAlert2 para erro ao buscar usuários
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: 'Não foi possível carregar os dados do usuário.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#1cb5e0'
          });
        });
    } else {
      console.error("Token não encontrado.");
      setLoading(false);
      
      // SweetAlert2 para token não encontrado
      Swal.fire({
        icon: 'warning',
        title: 'Acesso Negado',
        text: 'Token de autenticação não encontrado. Faça login novamente.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#1cb5e0'
      });
    }
  }, [userId]);

  // Função para enviar os dados atualizados do usuário
  const handleSave = () => {
    const token = getAuthToken();

    // Verifica se a role selecionada é a combinação de jornalista e publisher
    if (user.role === "journalist,publisher") {
      user.role = ["journalist", "publisher"];
    }

    if (token) {
      api
        .put(
          `api/updateUser/${userId}`, // Substitua com a URL da API para atualizar o usuário
          {
            name: user.name,
            email: user.email,
            password: newPassword, // Envia a nova senha, se fornecida
            role: user.role, // Envia a role como string ou array
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          // SweetAlert2 para sucesso
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Usuário atualizado com sucesso!',
            confirmButtonText: 'OK',
            confirmButtonColor: '#1cb5e0',
            timer: 3000,
            timerProgressBar: true
          });
        })
        .catch((error) => {
          console.error("Erro ao atualizar usuário:", error);
          
          // SweetAlert2 para erro na atualização
          Swal.fire({
            icon: 'error',
            title: 'Erro na Atualização',
            text: 'Não foi possível atualizar o usuário. Tente novamente.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#1cb5e0'
          });
        });
    }
  };

  // Atualiza o role quando selecionado no select
  const handleRoleChange = (e) => {
    const value = e.target.value;

    // Verifica se o valor selecionado é uma combinação de roles
    if (value === "journalist,publisher") {
      setUser({ ...user, role: ["journalist", "publisher"] }); // Trata como array
    } else {
      setUser({ ...user, role: value }); // Trata como string
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!user) {
    return <Typography>Usuário não encontrado</Typography>;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Sidebar */}
      <Box sx={{ width: 250 }}>
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Container maxWidth="lg">
          <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h4" fontWeight="bold" mb={3}>
              Editar Usuário: {user.name}
            </Typography>

            {/* Formulário de Edição */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nome"
                  value={user.name}
                  fullWidth
                  onChange={(e) => setUser({ ...user, name: e.target.value })} // Permite a edição
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  value={user.email}
                  fullWidth
                  onChange={(e) => setUser({ ...user, email: e.target.value })} // Permite a edição
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nova Senha"
                  type="password"
                  value={newPassword}
                  fullWidth
                  onChange={(e) => setNewPassword(e.target.value)} // Permite a edição da nova senha
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Função</InputLabel>
                  <Select
                    label="Função"
                    value={user.role === "journalist,publisher" ? "journalist,publisher" : user.role} // Valor adequado para role
                    onChange={handleRoleChange} // Atualiza o valor de role
                  >
                    {roles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: "#1cb5e0",
                    "&:hover": {
                      backgroundColor: "#000851",
                    },
                  }}
                  onClick={handleSave} // Salva as alterações
                >
                  Salvar Alterações
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default EditUserAdmin;